import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link, useBlocker } from "react-router-dom";
import api, { toArray } from "../lib/api";
import { toast } from "sonner";
import { AlertCircle, Save, X, Archive } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { norm } from "../lib/validation";

const s = (v) => (v == null ? "" : String(v));

const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RE_CF_PERSON = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/;
const RE_CF_COMPANY = /^\d{11}$/;
const RE_VAT_IT = /^\d{11}$/;

export default function ClientEditPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { has, user } = useAuth();
  const [subj, setSubj] = useState(null);
  const [lookups, setLookups] = useState(null);
  const [partners, setPartners] = useState([]);
  const [partnersErr, setPartnersErr] = useState(null);
  const [users, setUsers] = useState([]);
  const [personsForSelect, setPersonsForSelect] = useState([]);
  const [f, setF] = useState(null);
  const [initial, setInitial] = useState(null);
  const [err, setErr] = useState(null);
  const [fieldErr, setFieldErr] = useState({});
  const [dup, setDup] = useState(null);
  const [busy, setBusy] = useState(false);
  const allowNavigationRef = useRef(false);

  const loadPartners = () => {
    setPartnersErr(null);
    return api.get("/partners")
      .then(r => setPartners(toArray(r)))
      .catch(() => { setPartners([]); setPartnersErr("Impossibile caricare la lista Partner."); });
  };

  useEffect(() => {
    (async () => {
      const [sr, lk, us, ps] = await Promise.all([
        api.get(`/subjects/${id}`),
        api.get("/lookups/all"),
        api.get("/users").catch(() => ({ data: [] })),
        api.get("/subjects?subject_type=person&limit=200").catch(() => ({ data: { items: [] } })),
      ]);
      // Partner con retry dedicato (non-bloccante)
      loadPartners();
      setSubj(sr.data);
      setLookups(lk.data);
      setUsers(toArray(us));
      setPersonsForSelect(toArray(ps).filter((x) => x.id !== sr.data.id));

      const d = sr.data;
      const init = {
        // shared
        fiscal_code: s(d.fiscal_code),
        vat_number: s(d.vat_number),
        email: s(d.email), pec: s(d.pec), phone: s(d.phone), second_phone: s(d.second_phone),
        website: s(d.website), notes: s(d.notes),
        client_status: s(d.client_status) || "lead",
        acquisition_channel: s(d.acquisition_channel),
        referrer_partner_id: s(d.referrer_partner_id),
        assigned_user_id: s(d.assigned_user_id),
        privacy_consent: !!d.privacy_consent,
        privacy_consent_date: s(d.privacy_consent_date),
        privacy_consent_source: s(d.privacy_consent_source),
        privacy_revoked_at: s(d.privacy_revoked_at),
        privacy_notes: s(d.privacy_notes),
        last_contact_at: d.last_contact_at ? d.last_contact_at.slice(0, 16) : "",
        next_action_at: d.next_action_at ? d.next_action_at.slice(0, 16) : "",
        next_action_note: s(d.next_action_note),
        // person
        p_first_name: s(d.person?.first_name), p_last_name: s(d.person?.last_name),
        p_birth_date: s(d.person?.birth_date), p_birth_place: s(d.person?.birth_place),
        p_birth_province: s(d.person?.birth_province), p_gender: s(d.person?.gender),
        p_nationality: s(d.person?.nationality), p_profession: s(d.person?.profession),
        p_id_type: s(d.person?.identity_document_type),
        p_id_number: s(d.person?.identity_document_number),
        p_id_expiry: s(d.person?.identity_document_expiry),
        p_id_notes: s(d.person?.identity_document_notes),
        // company
        c_business_name: s(d.company?.business_name), c_legal_form: s(d.company?.legal_form),
        c_incorporation_date: s(d.company?.incorporation_date),
        c_activity_start_date: s(d.company?.activity_start_date),
        c_ateco: s(d.company?.ateco_code),
        c_ateco_secondary: (d.company?.ateco_secondary || []).join(", "),
        c_status: s(d.company?.company_status),
        c_size: s(d.company?.company_size),
        c_employees: s(d.company?.employees_count),
        c_revenue_last_year: s(d.company?.revenue_last_year),
        c_revenue_year: s(d.company?.revenue_year),
        c_rea: s(d.company?.rea_number),
        c_coc: s(d.company?.chamber_of_commerce),
        c_share_capital: s(d.company?.share_capital),
        c_main_contact_subject_id: s(d.company?.main_contact_subject_id),
        c_legal_rep_subject_id: s(d.company?.legal_rep_subject_id),
      };
      setF(init);
      setInitial(init);
    })();
  }, [id]);

  const tp = subj?.subject_type;

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const isDirty = useMemo(() => {
    if (!f || !initial) return false;
    return Object.keys(f).some((k) => (f[k] ?? "") !== (initial[k] ?? ""));
  }, [f, initial]);

  useEffect(() => {
    const h = (e) => { if (isDirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [isDirty]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    isDirty && !allowNavigationRef.current && currentLocation.pathname !== nextLocation.pathname
  );
  useEffect(() => {
    if (blocker && blocker.state === "blocked") {
      if (window.confirm("Hai modifiche non salvate. Vuoi uscire senza salvare?")) blocker.proceed();
      else blocker.reset();
    }
  }, [blocker?.state]); // eslint-disable-line

  // Duplicate check on CF/PIVA change (debounced)
  useEffect(() => {
    if (!f) return;
    const t = setTimeout(async () => {
      const fc = norm.cf(f.fiscal_code);
      const vn = norm.vat(f.vat_number);
      if (!fc && !vn) { setDup(null); return; }
      try {
        const r = await api.get("/subjects/check-duplicates", { params: { fiscal_code: fc, vat_number: vn, exclude_id: id } });
        setDup(r.data.duplicate);
      } catch { setDup(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [f?.fiscal_code, f?.vat_number, id]); // eslint-disable-line

  const validate = () => {
    const e = {};
    if (tp === "person") {
      if (!f.p_first_name.trim()) e.p_first_name = "Il nome è obbligatorio";
      if (!f.p_last_name.trim()) e.p_last_name = "Il cognome è obbligatorio";
    } else {
      if (!f.c_business_name.trim()) e.c_business_name = "La denominazione è obbligatoria";
    }
    if (f.email && !RE_EMAIL.test(f.email)) e.email = "Email non valida";
    if (f.pec && !RE_EMAIL.test(f.pec)) e.pec = "PEC non valida";
    const cf = norm.cf(f.fiscal_code);
    if (cf) {
      if (tp === "person" && !RE_CF_PERSON.test(cf) && !RE_CF_COMPANY.test(cf))
        e.fiscal_code = "Codice fiscale non valido (16 caratteri)";
      if (tp === "company" && !RE_CF_COMPANY.test(cf) && !RE_CF_PERSON.test(cf))
        e.fiscal_code = "Codice fiscale non valido";
    }
    const vt = norm.vat(f.vat_number);
    if (vt && !RE_VAT_IT.test(vt)) e.vat_number = "Partita IVA non valida (11 cifre)";
    if (f.p_birth_date && new Date(f.p_birth_date) > new Date()) e.p_birth_date = "Data futura non valida";
    if (f.c_incorporation_date && new Date(f.c_incorporation_date) > new Date()) e.c_incorporation_date = "Data futura non valida";
    if (f.c_employees && (isNaN(+f.c_employees) || +f.c_employees < 0)) e.c_employees = "Numero dipendenti non valido";
    if (f.c_share_capital && isNaN(parseFloat(f.c_share_capital))) e.c_share_capital = "Valore non valido";
    if (f.c_revenue_last_year && isNaN(parseFloat(f.c_revenue_last_year))) e.c_revenue_last_year = "Valore non valido";
    return e;
  };

  const buildPayload = (form) => {
    const str = (v) => (v === "" || v == null ? null : v);
    const uid = (v) => (v === "" || v == null ? null : v);
    const payload = {
      fiscal_code: norm.cf(form.fiscal_code) || null,
      vat_number: norm.vat(form.vat_number) || null,
      email: str(form.email), pec: str(form.pec),
      phone: str(form.phone), second_phone: str(form.second_phone),
      website: str(form.website), notes: str(form.notes),
      client_status: form.client_status,
      acquisition_channel: str(form.acquisition_channel),
      referrer_partner_id: uid(form.referrer_partner_id),
      assigned_user_id: uid(form.assigned_user_id),
      privacy_consent: !!form.privacy_consent,
      privacy_consent_date: str(form.privacy_consent_date),
      privacy_consent_source: str(form.privacy_consent_source),
      privacy_revoked_at: str(form.privacy_revoked_at),
      privacy_notes: str(form.privacy_notes),
      last_contact_at: form.last_contact_at ? new Date(form.last_contact_at).toISOString() : null,
      next_action_at: form.next_action_at ? new Date(form.next_action_at).toISOString() : null,
      next_action_note: str(form.next_action_note),
    };
    if (tp === "person") {
      payload.person = {
        first_name: form.p_first_name.trim(), last_name: form.p_last_name.trim(),
        birth_date: str(form.p_birth_date), birth_place: str(form.p_birth_place),
        birth_province: str(form.p_birth_province), gender: str(form.p_gender),
        nationality: str(form.p_nationality), profession: str(form.p_profession),
        identity_document_type: str(form.p_id_type),
        identity_document_number: str(form.p_id_number),
        identity_document_expiry: str(form.p_id_expiry),
        identity_document_notes: str(form.p_id_notes),
      };
    } else {
      const secList = form.c_ateco_secondary
        ? form.c_ateco_secondary.split(",").map((x) => x.trim()).filter(Boolean) : [];
      payload.company = {
        business_name: form.c_business_name.trim(),
        legal_form: str(form.c_legal_form),
        incorporation_date: str(form.c_incorporation_date),
        activity_start_date: str(form.c_activity_start_date),
        ateco_code: str(form.c_ateco),
        ateco_secondary: secList,
        company_status: str(form.c_status),
        company_size: str(form.c_size),
        employees_count: form.c_employees ? parseInt(form.c_employees) : null,
        revenue_last_year: form.c_revenue_last_year ? parseFloat(form.c_revenue_last_year) : null,
        revenue_year: form.c_revenue_year ? parseInt(form.c_revenue_year) : null,
        rea_number: str(form.c_rea),
        chamber_of_commerce: str(form.c_coc),
        share_capital: form.c_share_capital ? parseFloat(form.c_share_capital) : null,
        main_contact_subject_id: uid(form.c_main_contact_subject_id),
        legal_rep_subject_id: uid(form.c_legal_rep_subject_id),
      };
    }
    return payload;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    const errs = validate();
    setFieldErr(errs);
    if (Object.keys(errs).length) { setErr("Correggere gli errori evidenziati."); return; }
    if (dup) { setErr(`Duplicato: esiste già "${dup.display_name}" con questo CF/PIVA.`); return; }
    setBusy(true);
    try {
      await api.patch(`/subjects/${id}`, buildPayload(f));
      toast.success("Anagrafica aggiornata.");
      setInitial(f);
      allowNavigationRef.current = true;
      nav(`/clients/${id}`);
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      if (ex?.response?.status === 409 && d?.duplicate) setDup(d.duplicate);
      setErr(typeof d === "string" ? d : d?.message || "Errore nel salvataggio");
    } finally { setBusy(false); }
  };

  const archive = async () => {
    const activePractices = (subj.practices || []).length;
    let force = false;
    if (activePractices > 0) {
      if (user?.role !== "admin") {
        toast.error("Il cliente ha pratiche collegate. Solo un amministratore può forzare l'archiviazione.");
        return;
      }
      force = window.confirm(
        `⚠️ Attenzione: il cliente ha ${activePractices} pratica/e collegata/e. ` +
        "Come amministratore puoi comunque archiviare (procedura amministrativa). Confermi?"
      );
      if (!force) return;
    } else {
      if (!window.confirm(`Archiviare "${subj.display_name}"? Il record e tutte le relazioni verranno preservati e potranno essere ripristinati da un amministratore.`)) return;
    }
    try {
      await api.post(`/subjects/${id}/archive`, {}, { headers: force ? { "X-Admin-Force": "true" } : {} });
      toast.success("Cliente archiviato.");
      nav("/clients");
    } catch { /* handled */ }
  };

  if (!subj || !lookups || !f) return <div className="ma-loading">Caricamento…</div>;

  const Err = ({ k }) => fieldErr[k] ? <div className="ma-field-err"><AlertCircle size={12}/> {fieldErr[k]}</div> : null;

  return (
    <div className="ma-page">
      <div className="ma-page-header">
        <div>
          <div className="ma-crumb"><Link to={`/clients/${id}`}>← Scheda cliente</Link></div>
          <h1 className="ma-h1" data-testid="edit-client-title">Modifica {tp === "company" ? "impresa" : "persona"}: {subj.display_name}</h1>
          {isDirty && <div className="ma-sub"><span className="ma-pill pill-warn">Modifiche non salvate</span></div>}
        </div>
        <div className="ma-actions">
          {has("subjects.delete") && (
            <button type="button" className="ma-btn-danger-sm" onClick={archive} data-testid="btn-archive-client">
              <Archive size={14}/> Archivia
            </button>
          )}
        </div>
      </div>

      <form onSubmit={submit} className="ma-form-panel" noValidate>
        {tp === "person" ? (
          <>
            <h3 className="ma-h3">Dati anagrafici</h3>
            <div className="ma-form-grid">
              <div><label className="ma-label">Nome *</label>
                <input className={`ma-input ${fieldErr.p_first_name?"err":""}`} value={f.p_first_name} onChange={e=>set("p_first_name",e.target.value)} data-testid="edit-p-first"/>
                <Err k="p_first_name"/></div>
              <div><label className="ma-label">Cognome *</label>
                <input className={`ma-input ${fieldErr.p_last_name?"err":""}`} value={f.p_last_name} onChange={e=>set("p_last_name",e.target.value)} data-testid="edit-p-last"/>
                <Err k="p_last_name"/></div>
              <div><label className="ma-label">Codice fiscale</label>
                <input className={`ma-input ma-mono ${fieldErr.fiscal_code?"err":""}`} value={f.fiscal_code} onChange={e=>set("fiscal_code",e.target.value.toUpperCase())} data-testid="edit-cf"/>
                <Err k="fiscal_code"/></div>
              <div><label className="ma-label">Data di nascita</label>
                <input type="date" className={`ma-input ${fieldErr.p_birth_date?"err":""}`} value={f.p_birth_date} onChange={e=>set("p_birth_date",e.target.value)}/>
                <Err k="p_birth_date"/></div>
              <div><label className="ma-label">Luogo di nascita</label>
                <input className="ma-input" value={f.p_birth_place} onChange={e=>set("p_birth_place",e.target.value)}/></div>
              <div><label className="ma-label">Prov./Stato estero</label>
                <input className="ma-input" value={f.p_birth_province} onChange={e=>set("p_birth_province",e.target.value)}/></div>
              <div><label className="ma-label">Sesso</label>
                <select className="ma-input" value={f.p_gender} onChange={e=>set("p_gender",e.target.value)}>
                  <option value="">—</option><option value="M">Maschio</option><option value="F">Femmina</option><option value="X">Non specificato</option>
                </select></div>
              <div><label className="ma-label">Nazionalità</label>
                <input className="ma-input" value={f.p_nationality} onChange={e=>set("p_nationality",e.target.value)}/></div>
              <div><label className="ma-label">Professione</label>
                <input className="ma-input" value={f.p_profession} onChange={e=>set("p_profession",e.target.value)}/></div>
            </div>

            <h3 className="ma-h3">Documento di identità</h3>
            <div className="ma-form-grid">
              <div><label className="ma-label">Tipo documento</label>
                <select className="ma-input" value={f.p_id_type} onChange={e=>set("p_id_type",e.target.value)}>
                  <option value="">—</option>
                  {lookups.identity_document_types?.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
                </select></div>
              <div><label className="ma-label">Numero</label>
                <input className="ma-input" value={f.p_id_number} onChange={e=>set("p_id_number",e.target.value)}/></div>
              <div><label className="ma-label">Scadenza</label>
                <input type="date" className="ma-input" value={f.p_id_expiry} onChange={e=>set("p_id_expiry",e.target.value)}/></div>
              <div className="ma-col-full"><label className="ma-label">Note documento</label>
                <input className="ma-input" value={f.p_id_notes} onChange={e=>set("p_id_notes",e.target.value)}/></div>
            </div>
          </>
        ) : (
          <>
            <h3 className="ma-h3">Dati impresa</h3>
            <div className="ma-form-grid">
              <div className="ma-col-2"><label className="ma-label">Denominazione / Ragione sociale *</label>
                <input className={`ma-input ${fieldErr.c_business_name?"err":""}`} value={f.c_business_name} onChange={e=>set("c_business_name",e.target.value)} data-testid="edit-c-name"/>
                <Err k="c_business_name"/></div>
              <div><label className="ma-label">Partita IVA</label>
                <input className={`ma-input ma-mono ${fieldErr.vat_number?"err":""}`} value={f.vat_number} onChange={e=>set("vat_number",e.target.value)} data-testid="edit-vat"/>
                <Err k="vat_number"/></div>
              <div><label className="ma-label">Codice fiscale</label>
                <input className={`ma-input ma-mono ${fieldErr.fiscal_code?"err":""}`} value={f.fiscal_code} onChange={e=>set("fiscal_code",e.target.value.toUpperCase())}/>
                <Err k="fiscal_code"/></div>
              <div><label className="ma-label">Forma giuridica</label>
                <input className="ma-input" value={f.c_legal_form} onChange={e=>set("c_legal_form",e.target.value)}/></div>
              <div><label className="ma-label">Data costituzione</label>
                <input type="date" className={`ma-input ${fieldErr.c_incorporation_date?"err":""}`} value={f.c_incorporation_date} onChange={e=>set("c_incorporation_date",e.target.value)}/>
                <Err k="c_incorporation_date"/></div>
              <div><label className="ma-label">Inizio attività</label>
                <input type="date" className="ma-input" value={f.c_activity_start_date} onChange={e=>set("c_activity_start_date",e.target.value)}/></div>
              <div><label className="ma-label">Stato impresa</label>
                <select className="ma-input" value={f.c_status} onChange={e=>set("c_status",e.target.value)}>
                  <option value="">—</option>
                  {lookups.company_statuses?.map(s2 => <option key={s2.code} value={s2.code}>{s2.label}</option>)}
                </select></div>
              <div><label className="ma-label">ATECO principale</label>
                <input className="ma-input ma-mono" value={f.c_ateco} onChange={e=>set("c_ateco",e.target.value)}/></div>
              <div><label className="ma-label">ATECO secondari (virgola)</label>
                <input className="ma-input ma-mono" value={f.c_ateco_secondary} onChange={e=>set("c_ateco_secondary",e.target.value)}/></div>
              <div><label className="ma-label">Dimensione</label>
                <select className="ma-input" value={f.c_size} onChange={e=>set("c_size",e.target.value)}>
                  <option value="">—</option>
                  <option value="micro">Micro</option><option value="small">Piccola</option>
                  <option value="medium">Media</option><option value="large">Grande</option>
                </select></div>
              <div><label className="ma-label">Dipendenti</label>
                <input type="number" className={`ma-input ${fieldErr.c_employees?"err":""}`} value={f.c_employees} onChange={e=>set("c_employees",e.target.value)}/>
                <Err k="c_employees"/></div>
              <div><label className="ma-label">Numero REA</label>
                <input className="ma-input" value={f.c_rea} onChange={e=>set("c_rea",e.target.value)}/></div>
              <div><label className="ma-label">Camera di commercio</label>
                <input className="ma-input" value={f.c_coc} onChange={e=>set("c_coc",e.target.value)}/></div>
              <div><label className="ma-label">Capitale sociale (€)</label>
                <input type="number" step="0.01" className={`ma-input ${fieldErr.c_share_capital?"err":""}`} value={f.c_share_capital} onChange={e=>set("c_share_capital",e.target.value)}/>
                <Err k="c_share_capital"/></div>
              <div><label className="ma-label">Fatturato (€)</label>
                <input type="number" step="0.01" className={`ma-input ${fieldErr.c_revenue_last_year?"err":""}`} value={f.c_revenue_last_year} onChange={e=>set("c_revenue_last_year",e.target.value)}/>
                <Err k="c_revenue_last_year"/></div>
              <div><label className="ma-label">Anno fatturato</label>
                <input type="number" className="ma-input" value={f.c_revenue_year} onChange={e=>set("c_revenue_year",e.target.value)}/></div>
            </div>

            <h3 className="ma-h3">Riferimenti persone</h3>
            <div className="ma-form-grid">
              <div><label className="ma-label">Referente principale</label>
                <select className="ma-input" value={f.c_main_contact_subject_id} onChange={e=>set("c_main_contact_subject_id",e.target.value)}>
                  <option value="">—</option>
                  {personsForSelect.map(p => <option key={p.id} value={p.id}>{p.display_name}</option>)}
                </select></div>
              <div><label className="ma-label">Legale rappresentante</label>
                <select className="ma-input" value={f.c_legal_rep_subject_id} onChange={e=>set("c_legal_rep_subject_id",e.target.value)}>
                  <option value="">—</option>
                  {personsForSelect.map(p => <option key={p.id} value={p.id}>{p.display_name}</option>)}
                </select></div>
            </div>
          </>
        )}

        <h3 className="ma-h3">Contatti</h3>
        <div className="ma-form-grid">
          <div><label className="ma-label">Email</label>
            <input type="email" className={`ma-input ${fieldErr.email?"err":""}`} value={f.email} onChange={e=>set("email",e.target.value)}/>
            <Err k="email"/></div>
          <div><label className="ma-label">PEC</label>
            <input type="email" className={`ma-input ${fieldErr.pec?"err":""}`} value={f.pec} onChange={e=>set("pec",e.target.value)}/>
            <Err k="pec"/></div>
          <div><label className="ma-label">Telefono</label>
            <input className="ma-input" value={f.phone} onChange={e=>set("phone",e.target.value)}/></div>
          <div><label className="ma-label">Telefono secondario</label>
            <input className="ma-input" value={f.second_phone} onChange={e=>set("second_phone",e.target.value)}/></div>
          <div className="ma-col-2"><label className="ma-label">Sito web</label>
            <input className="ma-input" value={f.website} onChange={e=>set("website",e.target.value)}/></div>
        </div>

        {dup && (
          <div className="ma-warning" data-testid="dup-warning-edit">
            <AlertCircle size={16}/>
            <span>Esiste già un altro soggetto con CF/PIVA identici: <strong>{dup.display_name}</strong> — <Link to={`/clients/${dup.id}`} className="ma-link">apri</Link></span>
          </div>
        )}

        <h3 className="ma-h3">Gestione commerciale</h3>
        <div className="ma-form-grid">
          <div><label className="ma-label">Stato cliente</label>
            <select className="ma-input" value={f.client_status} onChange={e=>set("client_status",e.target.value)}>
              {lookups.client_statuses?.map(x => <option key={x.code} value={x.code}>{x.label}</option>)}
            </select></div>
          <div><label className="ma-label">Canale acquisizione</label>
            <select className="ma-input" value={f.acquisition_channel} onChange={e=>set("acquisition_channel",e.target.value)}>
              <option value="">—</option>
              {lookups.acquisition_channels?.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select></div>
          <div><label className="ma-label">Partner segnalatore</label>
            <select className="ma-input" value={f.referrer_partner_id} onChange={e=>set("referrer_partner_id",e.target.value)} data-testid="referrer-partner">
              <option value="">— {partnersErr ? "Errore caricamento" : "Nessuno"} —</option>
              {(Array.isArray(partners) ? partners : []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {partnersErr && (
              <div className="ma-notice" data-testid="partners-err" style={{marginTop:6,display:"flex",alignItems:"center",gap:8}}>
                <span>{partnersErr}</span>
                <button type="button" className="ma-btn-link" onClick={loadPartners} data-testid="partners-retry">Riprova</button>
              </div>
            )}
          </div>
          <div><label className="ma-label">Utente incaricato</label>
            <select className="ma-input" value={f.assigned_user_id} onChange={e=>set("assigned_user_id",e.target.value)}>
              <option value="">—</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
            </select></div>
          <div><label className="ma-label">Ultimo contatto</label>
            <input type="datetime-local" className="ma-input" value={f.last_contact_at} onChange={e=>set("last_contact_at",e.target.value)}/></div>
          <div><label className="ma-label">Prossima azione</label>
            <input type="datetime-local" className="ma-input" value={f.next_action_at} onChange={e=>set("next_action_at",e.target.value)}/></div>
          <div className="ma-col-full"><label className="ma-label">Descrizione prossima azione</label>
            <input className="ma-input" value={f.next_action_note} onChange={e=>set("next_action_note",e.target.value)}/></div>
        </div>

        <h3 className="ma-h3">Privacy</h3>
        <div className="ma-form-grid">
          <div className="ma-col-2">
            <label className="ma-checkbox">
              <input type="checkbox" checked={f.privacy_consent} onChange={e=>set("privacy_consent",e.target.checked)}/>
              <span>Consenso privacy acquisito</span>
            </label>
          </div>
          <div><label className="ma-label">Data consenso</label>
            <input type="date" className="ma-input" value={f.privacy_consent_date} onChange={e=>set("privacy_consent_date",e.target.value)}/></div>
          <div><label className="ma-label">Modalità/fonte</label>
            <input className="ma-input" value={f.privacy_consent_source} onChange={e=>set("privacy_consent_source",e.target.value)}/></div>
          <div><label className="ma-label">Data revoca</label>
            <input type="date" className="ma-input" value={f.privacy_revoked_at} onChange={e=>set("privacy_revoked_at",e.target.value)}/></div>
          <div className="ma-col-full"><label className="ma-label">Note privacy</label>
            <textarea className="ma-input" rows={2} value={f.privacy_notes} onChange={e=>set("privacy_notes",e.target.value)}/></div>
        </div>

        <h3 className="ma-h3">Note</h3>
        <textarea className="ma-input" rows={3} value={f.notes} onChange={e=>set("notes",e.target.value)}/>

        {err && <div className="ma-error" data-testid="edit-client-error">{err}</div>}
        <div className="ma-form-actions">
          <button type="button" className="ma-btn-secondary" onClick={() => {
            if (isDirty && !window.confirm("Annullare le modifiche non salvate?")) return;
            nav(`/clients/${id}`);
          }} data-testid="btn-cancel-edit-client"><X size={14}/> Annulla</button>
          <button type="submit" className="ma-btn-primary" disabled={busy || !isDirty} data-testid="btn-save-client">
            <Save size={14}/> {busy ? "Salvataggio…" : "Salva modifiche"}
          </button>
        </div>
      </form>
    </div>
  );
}
